<script setup lang="ts">
import type { HTMLImageElement } from "happy-dom"
import type { ComputedRef } from "vue"
import { useField } from "vee-validate"
import { computed, onBeforeUnmount, ref, toRef, useTemplateRef, watch } from "vue"
import { Icon } from "@/components/ui"
import { useTranslation } from "@/composables/useTranslation"

interface FileInputProps {
  name: string
  label?: string
  modelValue?: File | null
  accept?: string
  acceptMessage?: string
  preview?: string
  previewType?: "image" | "video"
  error?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<FileInputProps>(), {
  label: "",
  modelValue: undefined,
  accept: "",
  acceptMessage: "",
  error: "",
  preview: "",
  previewType: "image",
  disabled: false,
})

const emit = defineEmits<{
  "update:modelValue": [file?: File | null]
  "clear": []
}>()

const name = toRef(props, "name")

const { t } = useTranslation()

const { value, errorMessage, handleBlur, handleChange, handleReset } = useField<File | null | undefined>(name)

const previewEl = useTemplateRef<HTMLImageElement>("previewEl")
const videoPreviewEl = useTemplateRef<HTMLSourceElement>("videoPreviewEl")
const fileInput = useTemplateRef<HTMLInputElement>("fileInput")
const cleared = ref(false)
const videoPreviewError = ref(false)
const isDragging = ref(false)
const generatedImageUrl = ref<string>("")
const generatedVideoUrl = ref<string>("")

const labelId = computed(() => `${name.value}-label`)
const acceptMessageId = computed(() => `${name.value}-accept`)
const errorMessageId = computed(() => `${name.value}-error`)

const describedBy = computed(() => {
  const ids: string[] = []
  if (props.acceptMessage)
    ids.push(acceptMessageId.value)
  if (props.error || errorMessage.value)
    ids.push(errorMessageId.value)
  return ids.join(" ")
})

const currentFile = computed(() => props.modelValue ?? value.value)

function setFile(file?: File | null) {
  cleared.value = false
  videoPreviewError.value = false
  handleChange(file)
  emit("update:modelValue", file ?? null)
}

function onInput(event: Event) {
  const target = event.target as HTMLInputElement

  if (!target.files?.length)
    return clear()

  setFile(target.files[0])
}

function replaceImage() {
  if (props.disabled)
    return
  fileInput.value?.click()
}

function onDrop(event: DragEvent) {
  event.preventDefault()
  isDragging.value = false
  if (props.disabled || !event.dataTransfer?.files?.length)
    return

  setFile(event.dataTransfer.files[0])
}

function onDragOver(event: DragEvent) {
  event.preventDefault()
  if (props.disabled)
    return
  isDragging.value = true
}

function onDragLeave(event: DragEvent) {
  if (event.currentTarget === event.target)
    isDragging.value = false
}

function reloadVideoEl() {
  const parent = videoPreviewEl.value?.parentElement as HTMLVideoElement
  if (parent)
    parent.load()
}

function clear() {
  if (props.disabled)
    return
  cleared.value = true
  videoPreviewError.value = false
  handleReset()
  value.value = null
  if (fileInput.value)
    fileInput.value.value = ""
  if (previewEl.value)
    previewEl.value.removeAttribute("src")
  if (videoPreviewEl.value) {
    videoPreviewEl.value.removeAttribute("src")
    reloadVideoEl()
  }
  revokeGeneratedUrls()
  emit("update:modelValue", null)
  emit("clear")
}

const onVideoError = () => (videoPreviewError.value = true)

const showImage: ComputedRef<boolean> = computed(() => {
  if (cleared.value)
    return false
  if (props.preview && props.previewType === "image")
    return true
  const file = currentFile.value
  if (!file?.type.startsWith("image/"))
    return false
  return (
    Boolean(!props.error || !errorMessage.value)
    && (Boolean(value.value) || Boolean(props.modelValue))
  )
})

const showVideo: ComputedRef<boolean> = computed(() => {
  if (cleared.value)
    return false
  if (props.preview && props.previewType === "video")
    return true
  const file = currentFile.value
  if (!file?.type.startsWith("video/"))
    return false
  return (
    Boolean(!props.error || !errorMessage.value)
    && (Boolean(value.value) || Boolean(props.modelValue))
  )
})

const borderClass = computed<string>(() => {
  if (errorMessage.value || props.error)
    return "border-[#f04438]"
  if (showImage.value || showVideo.value)
    return "border-transparent"
  return "border-gray-300"
})

const dropzoneClass = computed(() =>
  isDragging.value ? "border-gray-400 bg-gray-50" : "bg-white",
)

const imagePreviewSrc = computed(
  () => generatedImageUrl.value || props.preview || "",
)
const videoPreviewSrc = computed(
  () => generatedVideoUrl.value || props.preview || "",
)

function revokeGeneratedUrls() {
  if (generatedImageUrl.value) {
    URL.revokeObjectURL(generatedImageUrl.value)
    generatedImageUrl.value = ""
  }
  if (generatedVideoUrl.value) {
    URL.revokeObjectURL(generatedVideoUrl.value)
    generatedVideoUrl.value = ""
  }
}

watch(
  currentFile,
  (file) => {
    revokeGeneratedUrls()

    if (file)
      cleared.value = false

    if (file?.type.startsWith("image/"))
      generatedImageUrl.value = URL.createObjectURL(file)

    if (file?.type.startsWith("video/"))
      generatedVideoUrl.value = URL.createObjectURL(file)

    if (videoPreviewEl.value && generatedVideoUrl.value)
      reloadVideoEl()
  },
  { immediate: true },
)

onBeforeUnmount(() => revokeGeneratedUrls())
</script>

<template>
  <div class="flex flex-col max-w-[375px] md:max-w-60">
    <label :id="labelId" :for="name">
      <slot name="label">
        <span class="font-comfortaa font-bold text-lg">{{ label }}</span>
      </slot>
    </label>
    <div
      class="relative rounded-xl flex flex-col items-center justify-center border aspect-9/10"
      :class="[
        borderClass,
        dropzoneClass,
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ]"
      @dragover="onDragOver"
      @dragenter="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <input
        :id="name"
        ref="fileInput"
        type="file"
        :accept="accept"
        :disabled="disabled"
        class="sr-only"
        tabindex="-1"
        aria-hidden="true"
        :aria-invalid="Boolean(error || errorMessage)"
        :aria-describedby="describedBy"
        @change="onInput"
        @blur="handleBlur"
      >
      <button
        type="button"
        class="absolute inset-0 z-10 rounded-xl flex flex-col items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        :class="[showVideo ? 'pointer-events-none' : '', disabled ? 'cursor-not-allowed' : '']"
        :disabled="disabled"
        :aria-labelledby="labelId"
        :aria-describedby="describedBy"
        :aria-invalid="Boolean(error || errorMessage)"
        @click="replaceImage"
      >
        <span
          v-show="!showImage && !showVideo"
          class="flex flex-col items-center justify-center"
        >
          <Icon icon="Upload" class="gpx-file-upload-icon" size="2xl" />
          <span class="text-primary text-xl font-medium">{{ t("file_input.tap_to_upload") }}</span>
          <span v-if="acceptMessage" :id="acceptMessageId" class="text-gray-600">{{ acceptMessage }}</span>
        </span>
      </button>
      <span class="absolute inset-0 z-0">
        <img
          v-show="showImage"
          ref="previewEl"
          :src="imagePreviewSrc"
          class="rounded-xl aspect-video object-cover h-full w-full block"
          :alt="t('upload_preview')"
          loading="lazy"
          data-test="file_preview"
        >
        <div v-show="showVideo" class="h-full w-full">
          <video
            v-show="!videoPreviewError"
            :id="`video-preview-${name}`"
            class="rounded-xl aspect-video object-cover h-full w-full block"
            controls
            muted
            playsinline
          >
            <source
              ref="videoPreviewEl"
              :src="videoPreviewSrc"
              @error="onVideoError"
            >
          </video>
          <div
            v-show="videoPreviewError"
            class="text-sm rounded-xl bg-gray-100 h-full w-full p-4 flex flex-col items-center justify-between overflow-auto"
          >
            <p>{{ t("file_input.preview_unavailable") }}</p>
            <Icon size="xl" icon="Video" />
            <p class="break-all line-clamp-2">
              {{ value?.name ?? props.preview }}
            </p>
          </div>
        </div>
      </span>
    </div>
    <div class="flex justify-around mt-2.5">
      <button
        v-if="preview || showImage || showVideo"
        type="button"
        class="font-comfortaa font-bold text-sm cursor-pointer flex gap-1 items-center hover:underline focus:underline text-link disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:no-underline"
        :disabled="disabled"
        @click.prevent="replaceImage"
      >
        <Icon icon="Redo" class="h-6 w-6" />
        <span>{{ t("file_input.replace") }}</span>
      </button>
      <button
        v-if="preview || showImage || showVideo"
        type="button"
        class="font-comfortaa font-bold text-sm flex gap-2 items-center hover:underline focus:underline text-error disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:no-underline"
        :disabled="disabled"
        @click.prevent="clear"
      >
        <Icon icon="Trash" class="h-6 w-6" />
        <span>{{ t("file_input.clear") }}</span>
      </button>
    </div>
    <span
      v-if="error || errorMessage"
      :id="errorMessageId"
      class="gpx-field-error"
    >
      {{ error || errorMessage }}
    </span>
  </div>
</template>

<style scoped>
.gpx-file-upload-icon {
  color: var(--template-secondary-colour);
}
</style>
